/**
 * Custom hook for managing content generation logic
 */

import { useState, useRef, useEffect } from 'react'
import { api } from '../../../../services'
import { config } from '../../../../constants'
import { message } from 'antd'
import history from '../../../../history'
import {
  selectItemsWithGeneratedContentEmpty,
  convertToArrayOfObjects,
  convertContentToCourseData,
  removeReferences
} from '../utils'

export const useContentGeneration = ({
  content,
  setContent,
  courseTitle,
  courseId,
  user,
  onContentGenerationStarted,
  setIsContentGenerationCompleted,
  contentChoice = [],
  language
}) => {
  const [isGeneratingContent, setIsGeneratingContent] = useState(false)
  const [isGeneratingCourse, setIsGeneratingCourse] = useState(false)
  const [isSavingContent, setIsSavingContent] = useState(false)
  const [isAutoUpdate, setIsAutoUpdate] = useState(true)
  const socketRef = useRef()

  console.log('use content generation', language)

  useEffect(() => {
    return () => {
      socketRef.current && socketRef.current.close()
    }
  }, [])

  useEffect(() => {
    if (isGeneratingContent) return

    // if any subtopic has generatedContent property
    const hasGeneratedContent = Object.keys(content).some(section => {
      return Object.keys(content[section]).some(subtopic => {
        return (
          content[section][subtopic] &&
          content[section][subtopic].generatedContent
        )
      })
    })

    if (hasGeneratedContent && isAutoUpdate) {
      saveContentToDatabase()
    }
  }, [content, isGeneratingContent, isAutoUpdate])

  const saveContentToDatabase = data => {
    const contentToSave = JSON.stringify(data || content)
    const { active_organization_id } = user.info

    setIsSavingContent(true)
    api.courses
      .saveGeneratedContent({
        courseId: courseId,
        orgId: active_organization_id,
        table: { content: contentToSave },
        language
        // file_list: contentChoice
      })
      .then(() => {
        setIsSavingContent(false)
      })
      .catch(err => {
        setIsSavingContent(false)
        console.log(err)
      })
  }

  const initializeWebsocket = () => {
    const { id, username } = user.info
    socketRef.current = new WebSocket(
      config.midJourneySocket +
        `?channel_type=COURSE&channel_id=${courseId}&user_id=${id}&user_name=${username}`
    )

    socketRef.current.onopen = () => {
      console.log('Websocket open')
      sendContentGenerationRequest()
    }

    socketRef.current.onclose = () => {
      console.log('Websocket closed')
    }

    socketRef.current.onmessage = event => {
      const data = JSON.parse(event.data)

      if (!data.content) return

      const receivedContent = [...data.content]

      if (
        (receivedContent && receivedContent.length === 0) ||
        (data && data.generate_status === 'error')
      ) {
        setIsGeneratingCourse(false)
        socketRef.current.close()
        handleGenerationCompletion(content)
        return
      }

      const newContent = { ...content }

      Object.entries(content).forEach(([key, value]) => {
        receivedContent.forEach(item => {
          if (item.topic === key) {
            // Ensure value is an object and has sub_topic keys
            if (typeof value === 'object' && !Array.isArray(value)) {
              Object.keys(value).forEach(sub_topic => {
                if (sub_topic === item.sub_topic) {
                  if (!newContent[key][sub_topic]) {
                    newContent[key][sub_topic] = {}
                  }

                  console.log({
                    key,
                    sub_topic,
                    item,
                    newContent
                  })

                  // Update or initialize 'generatedContent' and 'status'
                  newContent[key][sub_topic]['generatedContent'] =
                    item.content_generated
                  newContent[key][sub_topic]['status'] = item.generated_status
                  newContent[key][sub_topic]['img_path'] = item.img_path
                  newContent[key][sub_topic]['references'] = item.reference_urls
                }
              })
            }
          }
        })
      })

      console.log({ newContent })

      setContent(newContent)
      saveContentToDatabase(newContent)

      if (data.generate_status === 'completed') {
        socketRef.current.close()
        handleGenerationCompletion(newContent)
      }
    }
  }

  const handleGenerationCompletion = newContent => {
    const contentToProcess = { ...newContent }

    Object.keys(contentToProcess).forEach(topic => {
      Object.keys(contentToProcess[topic]).forEach(sub_topic => {
        const contentObj =
          contentToProcess &&
          contentToProcess[topic] &&
          contentToProcess[topic][sub_topic]
        if (contentObj && contentObj.generatedContent) {
          contentObj.status = 'success'
        } else {
          contentObj.generatedContent = ''
          contentObj.status = 'error'
        }
      })
    })

    setIsGeneratingContent(false)

    // Return the modified content
    setContent(contentToProcess)
    saveContentToDatabase(contentToProcess)
  }

  const sendContentGenerationRequest = () => {
    const { active_organization_id, id } = user.info

    const contentToGenerate = selectItemsWithGeneratedContentEmpty(content)
    const structuredArray = convertToArrayOfObjects(contentToGenerate)

    api.courses
      .aiContentGenerationUrl({
        language,
        course_id: courseId,
        table_of_content: JSON.stringify(content),
        course_name: courseTitle,
        org_id: String(active_organization_id),
        table_of_contents: structuredArray,
        file_list: contentChoice?.map(f => f.path),
        user_id: id
      })
      .then(res => {
        console.log({ res })
      })
      .catch(err => {
        setIsGeneratingContent(false)
        console.error(err)
      })
  }

  const handleGenerateCourseContent = () => {
    setIsGeneratingContent(true)
    onContentGenerationStarted()
    initializeWebsocket()
  }

  const handleCreateCourse = templateData => {
    setIsGeneratingCourse(true)
    const contentWithRemovedRefs = removeReferences(content)
    const structuredArray = convertContentToCourseData(contentWithRemovedRefs)
    templateData.tocs = structuredArray

    // Create a queue of promises to process each topic one by one
    const processTopicsSequentially = async () => {
      try {
        for (let i = 0; i < structuredArray.length; i++) {
          // Create a new payload for each topic
          const singleTopicPayload = {
            ...templateData,
            position: i + 1,
            tocs: [structuredArray[i]] // Send only one topic at a time,
          }

          // Log which topic is being processed (optional)
          console.log(
            `Processing topic ${i + 1}/${structuredArray.length}: ${
              structuredArray[i].topic
            }`
          )

          // Make the API call for this single topic
          await api.courses.generateAllAILessons(
            { courseId },
            singleTopicPayload
          )
        }

        // All topics processed successfully
        history.push(`/courses/${courseId}/edit`)
      } catch (err) {
        setIsGeneratingCourse(false)
        message.error(err.message)
      }
    }

    // Start the sequential processing
    processTopicsSequentially()
  }

  return {
    isGeneratingContent,
    setIsGeneratingContent,
    isGeneratingCourse,
    setIsGeneratingCourse,
    isSavingContent,
    setIsSavingContent,
    isAutoUpdate,
    setIsAutoUpdate,
    saveContentToDatabase,
    handleGenerateCourseContent,
    handleCreateCourse
  }
}
